import moment from "moment";
import camelCase from "lodash/camelCase";
import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  AccountPhoneNumberStatusesEnum,
  BrokerLicenseStatusesEnum,
  DATE_MONTH_FORMAT,
  DashFeaturesEnum,
  ProductSubscriptionStatusesEnum,
} from "dash-common";
import {
  BannerTypesEnum,
  DashboardNotificationBannerContext,
  ViewerContext,
  useCheckIfSubscriptionFeatureActiveBroker,
} from "dash-ui";

import { RoutesPath } from "flow-components/routes/routes";
import { ProfileTabsKeys } from "pages/ProfilePage/ProfilePage.constants";

import {
  checkIfBrokerInfoCompleted,
  checkIfNotificationCanBeShown,
  removeNotificationDismissTimestamp,
  setNotificationDismissTimestamp,
} from "./useCreateDashboardNotifications.helpers";
import { DashboardNotificationsEnum } from "./useCreateDashboardNotifications.constants";
import { ICreateDashboardNotifications } from "./useCreateDashboardNotifications.interfaces";
import { useGetSubscriptionProductsQuery } from "gql/generated.graphql";

export const useCreateDashboardNotifications = () => {
  const { t } = useTranslation("pages");
  const navigate = useNavigate();
  const { broker, profile, account, subscription } = useContext(ViewerContext);
  const { setNotification, removeNotification } = useContext(
    DashboardNotificationBannerContext
  );
  const { checkFeatureAccess, triggerSubscriptionModal, isSubscriptionActive } =
    useCheckIfSubscriptionFeatureActiveBroker([
      DashFeaturesEnum.ANALYTICS_AND_REPORTING_TOOLS,
    ]);
  const hasAccess = checkFeatureAccess({ disableModal: true });

  const createNotification = ({
    key,
    onViewClick,
    notificationType,
    condition,
    date,
    submitButtonLabel,
  }: ICreateDashboardNotifications) => {
    const showMessage = checkIfNotificationCanBeShown(key);

    if (condition && showMessage) {
      setNotification({
        itemId: key,
        message: t(
          `pages:dashboardPage.banner.notifications.${camelCase(key)}`,
          { date }
        ),
        onViewClick,
        type: notificationType,
        submitButtonLabel,
        onDismissClick: () => {
          removeNotification(key);
          setNotificationDismissTimestamp(key);
        },
      });
    } else {
      removeNotification(key);
      if (!condition && !showMessage) {
        removeNotificationDismissTimestamp(key);
      }
    }
  };

  // Workaround to hide banner when user is only changing subscription recurring types.
  const { data: data } = useGetSubscriptionProductsQuery({
    variables: { filter: { stripePriceId: subscription?.downgradeTo } },
    skip: !subscription?.downgradeTo,
  });
  const downgradeProduct = data?.getSubscriptionProducts.find(
    (e) => e.stripePriceId === subscription?.downgradeTo
  );

  const showNotificationsBanners = useCallback(() => {
    // notification if user has no access to dashboard
    createNotification({
      key: DashboardNotificationsEnum.UPGRADE_SUBSCRIPTION,
      onViewClick: () => {
        triggerSubscriptionModal({
          onCompleted: () => {
            removeNotification(DashboardNotificationsEnum.UPGRADE_SUBSCRIPTION);
          },
        });
      },
      notificationType: BannerTypesEnum.WARNING,
      condition: !hasAccess && !!isSubscriptionActive,
    });

    // notification if some  user information was not provided
    const isCompleted = !checkIfBrokerInfoCompleted(broker, profile);

    createNotification({
      key: DashboardNotificationsEnum.BROKER_INFO_INCOMPLETE,
      onViewClick: () => {
        navigate(`${RoutesPath.Profile}?tab=${ProfileTabsKeys.ACCOUNT_INFO}`);
      },
      notificationType: BannerTypesEnum.WARNING,
      condition: isCompleted,
    });

    // notification if phone number not verified
    createNotification({
      key: DashboardNotificationsEnum.PHONE_VERIFICATION,
      onViewClick: () => {
        navigate(`${RoutesPath.Profile}?tab=${ProfileTabsKeys.ACCOUNT_INFO}`);
      },
      notificationType: BannerTypesEnum.WARNING,
      condition:
        account?.phone?.status === AccountPhoneNumberStatusesEnum.NOT_VERIFIED,
    });

    // notification if broker license is inactive
    createNotification({
      key: DashboardNotificationsEnum.BROKER_LICENSE,
      onViewClick: () => {
        navigate(`${RoutesPath.Profile}?tab=${ProfileTabsKeys.ACCOUNT_INFO}`);
      },
      notificationType: BannerTypesEnum.ERROR,
      condition:
        broker?.license?.licenseStatus === BrokerLicenseStatusesEnum.INACTIVE,
    });

    const isCanceled =
      subscription?.status ===
        ProductSubscriptionStatusesEnum.SCHEDULED_CANCELLATION &&
      downgradeProduct?.price === 0 &&
      moment().isBefore(subscription?.currentPeriodEndAt);

    createNotification({
      key: DashboardNotificationsEnum.CANCEL_SUBSCRIPTION,
      onViewClick: () => {
        navigate(`${RoutesPath.Profile}?tab=${ProfileTabsKeys.BILLING}`);
      },
      submitButtonLabel: t("pages:dashboardPage.banner.renew") || "",
      notificationType: BannerTypesEnum.ERROR,
      condition: isCanceled,
      date: moment(subscription?.currentPeriodEndAt).format(DATE_MONTH_FORMAT),
    });

    // notification about trial period expiration
    const dateFrom = moment(subscription?.trialEndAt).subtract(7, "d");
    const dateTo = moment(subscription?.trialEndAt);
    const isExpired =
      subscription?.status === ProductSubscriptionStatusesEnum.TRIALING &&
      moment().isBetween(dateFrom, dateTo);

    createNotification({
      key: DashboardNotificationsEnum.TRIAL_EXPIRED,
      onViewClick: () => {
        navigate(`${RoutesPath.Profile}?tab=${ProfileTabsKeys.BILLING}`);
      },
      notificationType: BannerTypesEnum.SUCCESS,
      condition: isExpired,
      date: dateTo.format(DATE_MONTH_FORMAT),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, isSubscriptionActive, broker, profile, account, subscription, downgradeProduct]);

  return { showNotificationsBanners };
};
